import type { PostgrestError } from '@supabase/supabase-js';
import { safeErrorMessage } from './appErrors';

type RelationError = Partial<PostgrestError> & { status?: number };

/** Explicit access/session denials must never be treated as a transient read
 * failure that permits displaying a cached private snapshot. */
export function isAccessDeniedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as RelationError & { statusCode?: number; kind?: string; name?: string };
  return (candidate.code === 'P0001' && candidate.message === 'not_authorized')
    || ['42501', 'PGRST301', 'PGRST302', 'PGRST303', 'bad_jwt', 'invalid_jwt'].includes(candidate.code ?? '')
    || ['access_denied', 'session_expired'].includes(candidate.kind ?? '')
    || ['AuthSessionMissingError', 'AuthInvalidJwtError', 'AuthInvalidTokenResponseError'].includes(candidate.name ?? '')
    || [401, 403].includes(Number(candidate.status ?? candidate.statusCode));
}

export function isMissingRelationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as RelationError;
  const code = typeof candidate.code === 'string' ? candidate.code : undefined;
  if (code === 'PGRST301' || code === 'PGRST302' || code === 'PGRST303') return false;
  if (code === 'PGRST205' || code === '42P01') return true;
  const messageParts = [candidate.message, candidate.details, candidate.hint]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')
    .toLowerCase();
  return messageParts.includes('does not exist') || messageParts.includes('no relation found');
}

export function isMissingColumnError(error: unknown, columnName?: string): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as RelationError;
  const code = typeof candidate.code === 'string' ? candidate.code : undefined;
  if (code === 'PGRST301' || code === 'PGRST302' || code === 'PGRST303') return false;
  if (code === '42703' || code === 'PGRST204') return true;
  const messageParts = [candidate.message, candidate.details, candidate.hint]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')
    .toLowerCase();
  const columnMatch = columnName ? messageParts.includes(columnName.toLowerCase()) : true;
  return columnMatch && (messageParts.includes('column') || messageParts.includes('schema cache'));
}

export function formatSupabaseUiError(error: unknown, fallback: string): string {
  return safeErrorMessage(error, fallback);
}
